import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { berlinDate, isReceiptDataset, type ExportDatasetKey, type ReceiptDatasetKey } from '@kontor/shared';
import { api } from '../../api';
import { Stepper, Tip } from './Stepper';

const STEPS = ['Auswählen', 'Einstellen', 'Herunterladen'];
const dayDe = (d: string) => d.split('-').reverse().join('.');

/**
 * Export als geführter Ablauf (KON-171): Auswählen (alles oder einzelne Bereiche), Einstellen (Bereiche und Dokumente), Herunterladen. Ein einzelner Bereich kommt als CSV, alles andere als
 * ZIP mit Liesmich-Datei. Das Herunterladen übernimmt der Browser, die Seite wartet nicht darauf.
 * „Belege als Dateien“ liefert Ausgangsrechnungen als PDF oder Eingangsrechnungen als Originale in einem ZIP, je Monat ein Ordner, für einen Zeitraum nach Rechnungsdatum.
 */
export function ExportWizard({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState<'all' | 'some' | 'receipts'>('all');
  const [documents, setDocuments] = useState(false);
  const [picked, setPicked] = useState<Set<ExportDatasetKey>>(new Set());
  // Belege: welcher Bereich und welcher Zeitraum, Vorgabe ist das laufende Jahr
  const year = Number(berlinDate().slice(0, 4));
  const [receipt, setReceipt] = useState<ReceiptDatasetKey>('sales_invoices');
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(`${year}-12-31`);
  const datasets = useQuery({ queryKey: ['export-datasets'], queryFn: async () => (await api<{ data: { key: ExportDatasetKey; label: string; note: string | null }[] }>('GET', '/imports/export')).data });
  const list = datasets.data ?? [];

  const chosen = mode === 'all' ? list.map((d) => d.key) : list.filter((d) => picked.has(d.key)).map((d) => d.key);
  const single = mode === 'some' && chosen.length === 1;
  const receiptOptions = list.filter((d) => isReceiptDataset(d.key));
  const receiptLabel = list.find((d) => d.key === receipt)?.label ?? '';
  const rangeOk = !!from && !!to && to >= from;
  const setYear = (y: number) => {
    setFrom(`${y}-01-01`);
    setTo(`${y}-12-31`);
  };
  // Belege als ZIP, ein einzelner Bereich ohne Dokumente direkt als CSV, sonst als ZIP
  const href =
    mode === 'receipts'
      ? `/api/v1/imports/export/${receipt}/belege.zip?from=${from}&to=${to}`
      : single && !documents
        ? `/api/v1/imports/export/${chosen[0]}`
        : `/api/v1/imports/export/all.zip?documents=${documents ? 1 : 0}${mode === 'some' ? `&datasets=${chosen.join(',')}` : ''}`;
  const toggle = (key: ExportDatasetKey) => setPicked((cur) => { const next = new Set(cur); next.has(key) ? next.delete(key) : next.add(key); return next; });
  const hasDocuments = list.some((d) => d.key === 'documents');
  const asCsv = mode === 'some' && single && !documents;

  return (
    <section className="card wiz" aria-labelledby="wiz-title">
      <div className="wiz-head">
        <h2 id="wiz-title">Daten exportieren</h2>
        {step < 2 && <button type="button" className="link-button" onClick={onClose}>Abbrechen</button>}
      </div>
      <Stepper steps={STEPS} current={step} />

      {step === 0 && (
        <div className="wiz-body">
          <h3>Was möchten Sie exportieren?</h3>
          <div className="wiz-choices" role="radiogroup" aria-label="Umfang">
            <button type="button" role="radio" aria-checked={mode === 'all'} className={`wiz-choice${mode === 'all' ? ' is-on' : ''}`} onClick={() => setMode('all')}>
              <strong>Alles</strong>
              <span>Alle Bereiche Ihres Pakets in einem ZIP, mit Liesmich-Datei. Auf Wunsch mit den Dokumenten. Gut zur Sicherung und für den Steuerberater.</span>
            </button>
            <button type="button" role="radio" aria-checked={mode === 'some'} className={`wiz-choice${mode === 'some' ? ' is-on' : ''}`} onClick={() => setMode('some')}>
              <strong>Einzelne Bereiche</strong>
              <span>Zum Beispiel nur die Kontakte oder die Rechnungen. Ein Bereich kommt als CSV-Datei für Excel, mehrere als ZIP.</span>
            </button>
            {receiptOptions.length > 0 && (
              <button
                type="button"
                role="radio"
                aria-checked={mode === 'receipts'}
                className={`wiz-choice${mode === 'receipts' ? ' is-on' : ''}`}
                onClick={() => {
                  setMode('receipts');
                  if (!receiptOptions.some((d) => d.key === receipt)) setReceipt(receiptOptions[0]!.key as ReceiptDatasetKey);
                }}
              >
                <strong>Belege als Dateien</strong>
                <span>Rechnungen als PDF in einem ZIP, je Monat ein Ordner, mit Belegliste. Für den Steuerberater, eine Betriebsprüfung oder wenn das Finanzamt die Rechnungen sehen will.</span>
              </button>
            )}
          </div>
          <Tip title="Gut zu wissen">Die Dateien sind für Excel gemacht: Semikolon als Trennzeichen, Zahlen mit Komma, Umlaute stimmen. Kontakte und Produkte haben das Format der Importvorlagen, Sie können sie also bearbeiten und wieder einlesen. Jeder Export steht im Änderungsprotokoll.</Tip>
          <div className="wiz-nav"><span /><button type="button" className="btn btn--primary" onClick={() => setStep(1)}>Weiter</button></div>
        </div>
      )}

      {step === 1 && (
        <div className="wiz-body">
          <h3>{mode === 'all' ? 'Einstellungen' : mode === 'receipts' ? 'Belege und Zeitraum' : 'Bereiche wählen'}</h3>
          {mode === 'receipts' && (
            <>
              <div className="wiz-choices" role="radiogroup" aria-label="Welche Belege">
                {receiptOptions.map((d) => (
                  <button key={d.key} type="button" role="radio" aria-checked={receipt === d.key} className={`wiz-choice${receipt === d.key ? ' is-on' : ''}`} onClick={() => setReceipt(d.key as ReceiptDatasetKey)}>
                    <strong>{d.label}</strong>
                    <span>{d.key === 'sales_invoices' ? 'Die ausgestellten PDF, wie Ihre Kunden sie bekommen haben, mit der E-Rechnung im Anhang.' : 'Die hochgeladenen Originale Ihrer Lieferanten, E-Rechnungen zusätzlich als XML.'}</span>
                  </button>
                ))}
              </div>
              <fieldset className="wiz-range">
                <legend>Zeitraum nach Rechnungsdatum</legend>
                <div className="wiz-actions">
                  <button type="button" className="btn btn--secondary btn--sm" onClick={() => setYear(year)}>Dieses Jahr</button>
                  <button type="button" className="btn btn--secondary btn--sm" onClick={() => setYear(year - 1)}>Letztes Jahr</button>
                </div>
                <div className="form-grid">
                  <label className="field"><span>Von</span><input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
                  <label className="field"><span>Bis</span><input className="input" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} aria-invalid={!rangeOk} /></label>
                </div>
                {!rangeOk && <div className="alert alert--error" role="alert">Das Ende liegt vor dem Beginn.</div>}
              </fieldset>
              <Tip title="Was im ZIP steckt">Nur ausgestellte beziehungsweise freigegebene Rechnungen, Entwürfe sind noch keine Belege. Eine Belegliste als CSV nennt jede Rechnung mit Beträgen und Dateinamen, die Liesmich-Datei nennt, was fehlt, zum Beispiel von Hand erfasste Eingangsrechnungen ohne hochgeladenen Beleg. Höchstens 300 MB je Download, bei mehr wählen Sie einen kürzeren Zeitraum.</Tip>
            </>
          )}
          {mode === 'some' && (
            <>
              <div className="wiz-actions">
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => setPicked(new Set(list.map((d) => d.key)))}>Alle wählen</button>
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => setPicked(new Set())}>Keinen wählen</button>
                <span className="muted small">{chosen.length} von {list.length} gewählt</span>
              </div>
              {datasets.isLoading && <p className="muted">Wird geladen …</p>}
              <ul className="wiz-checklist">
                {list.map((d) => (
                  <li key={d.key}>
                    <label>
                      <input type="checkbox" checked={picked.has(d.key)} onChange={() => toggle(d.key)} />
                      <span><strong>{d.label}</strong>{d.note && <span className="muted small"> · {d.note}</span>}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </>
          )}
          {mode === 'all' && <p>Enthalten sind {list.length} Bereiche Ihres Pakets: {list.slice(0, 6).map((d) => d.label).join(', ')}{list.length > 6 ? ' und weitere' : ''}.</p>}
          {hasDocuments && mode !== 'receipts' && (
            <label className="imp-check wiz-option">
              <input type="checkbox" checked={documents} onChange={(e) => setDocuments(e.target.checked)} />
              <span><strong>Die Dokumente dazunehmen</strong><span className="muted small"> – die Dateien aus dem Modul Dokumente, bis 300 MB. Der Download kann dann etwas dauern.</span></span>
            </label>
          )}
          {mode !== 'receipts' && <Tip title="Nicht enthalten">Von den Mitarbeitern kommen nur die Stammdaten, ohne Steuer-, Bank- und Sozialdaten. Lohn- und Vergütungsdaten gehören nicht zum Export.</Tip>}
          <div className="wiz-nav">
            <button type="button" className="btn btn--secondary" onClick={() => setStep(0)}>Zurück</button>
            <button type="button" className="btn btn--primary" disabled={mode === 'receipts' ? !rangeOk : chosen.length === 0} onClick={() => setStep(2)}>{mode !== 'receipts' && chosen.length === 0 ? 'Bitte Bereich wählen' : 'Weiter'}</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="wiz-body">
          <h3>Herunterladen</h3>
          {mode === 'receipts' ? (
            <p>Sie laden die <strong>{receiptLabel}</strong> vom {dayDe(from)} bis {dayDe(to)} als Dateien in einem ZIP herunter. Bei vielen Belegen kann der Download etwas dauern.</p>
          ) : (
            <p>
              Sie laden {asCsv ? <>die Datei <strong>{list.find((d) => d.key === chosen[0])?.label}</strong> als CSV</> : <>ein ZIP mit <strong>{chosen.length} Bereichen</strong>{documents ? ' und den Dokumenten' : ''}</>} herunter.
            </p>
          )}
          <div className="wiz-actions"><a className="btn btn--primary" href={href}>{asCsv ? 'CSV herunterladen' : 'ZIP herunterladen'}</a></div>
          {asCsv && isReceiptDataset(chosen[0]!) && <Tip title="Rechnungen als PDF?">Die CSV-Datei ist eine Liste. Brauchen Sie die Rechnungen selbst, zum Beispiel für das Finanzamt, wählen Sie im ersten Schritt „Belege als Dateien“.</Tip>}
          <Tip tone="warn" title="Bitte sorgfältig aufbewahren">Der Export enthält personenbezogene und geschäftliche Daten. Speichern Sie ihn an einem geschützten Ort und schicken Sie ihn nicht unverschlüsselt per E-Mail. Löschen Sie ihn, wenn Sie ihn nicht mehr brauchen.</Tip>
          <div className="wiz-nav">
            <button type="button" className="btn btn--secondary" onClick={() => setStep(1)}>Zurück</button>
            <button type="button" className="btn btn--secondary" onClick={onClose}>Fertig</button>
          </div>
        </div>
      )}
    </section>
  );
}
